/**
 * useActionsRapides — construit la liste d'actions du Fab (« + ») du dashboard,
 * filtrée par PERMISSION + CONTEXTE (règle terrain : ne jamais proposer une action
 * impossible). Le Fab (`@champ-erp/ui`) reste une présentation pure : c'est ici
 * qu'on résout droits, contexte et navigation.
 *
 * Actions candidates :
 *   - « Pesée »        → si `vendange.pesee_create` ET une campagne est ouverte.
 *   - « Vente caveau » → si `vente.create`.
 *   - « Nouveau devis »→ si `vente.create`.
 */

import type { FabAction } from "@champ-erp/ui";
import { useQuery } from "@tanstack/react-query";
import { FileText, Grape, Store } from "lucide-react";
import { createElement } from "react";
import { useNavigate } from "react-router-dom";

import { useCan } from "../../auth/use-can.js";
import { trpc } from "../../lib/trpc.js";
import { ROUTES_VENTES } from "../ventes/shared/routes.js";

export function useActionsRapides(): FabAction[] {
  const navigate = useNavigate();
  const { granted: peutSaisirPesee } = useCan("vendange.pesee_create");
  const { granted: peutCreerVente } = useCan("vente.create");

  // Campagne ouverte ? On ne propose « Pesée » que si la saisie est possible.
  // Requête gardée par le droit de pesée (et non-bloquante : retry désactivé).
  const { data: campagnesOuvertes } = useQuery({
    queryKey: ["campagnes.lister", { statut: "ouverte" }],
    queryFn: () => trpc.campagnes.lister.query({ statut: "ouverte" }),
    enabled: peutSaisirPesee,
    retry: false,
  });
  const aCampagneOuverte = (campagnesOuvertes?.length ?? 0) > 0;

  const actions: FabAction[] = [];

  if (peutSaisirPesee && aCampagneOuverte) {
    actions.push({
      label: "Pesée",
      description: "Saisir une pesée de vendange",
      // createElement plutôt que JSX : ce fichier est un module .ts (pas .tsx).
      icon: createElement(Grape, { size: 18 }),
      onSelect: () => navigate("/vendange/pesee/nouvelle"),
    });
  }

  if (peutCreerVente) {
    actions.push({
      label: "Vente caveau",
      description: "Vente rapide au caveau",
      icon: createElement(Store, { size: 18 }),
      onSelect: () => navigate(ROUTES_VENTES.caveau),
    });
    actions.push({
      label: "Nouveau devis",
      description: "Créer un devis client",
      icon: createElement(FileText, { size: 18 }),
      onSelect: () => navigate(ROUTES_VENTES.nouveauDocument),
    });
  }

  return actions;
}
